/**
 * The one small WebGL renderer the captain is drawn with, shared by every place he appears (the bubble's bust, the
 * welcome card, the Guide sheet's replay card): its canvas moves into whichever slot is current. It renders only
 * while that slot is on screen and the page is visible (0 fps otherwise), at 30 fps when he is only breathing and
 * 60 while he talks or gestures, and it lets go of the GPU entirely when the guide has been quiet for a while. A lost
 * context (iOS drops them under memory pressure and in the background) is replaced on the next use; repeated losses
 * or no WebGL at all leave the static renders in place for the session.
 */
import * as THREE from "three";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";
import type { GuideKind } from "../guide";
import { BramAnimator } from "./bramAnimator";
import { forgetBram, loadBram, type BramModel } from "./bramAsset";
import type { BramMood } from "./bramBrain";
import { bramFps, bramPixelRatio, type TierBandName } from "./bramFallback";
import type { Speech } from "./bramSpeech";
import { DEFAULT_NECK_Y, DEFAULT_HEAD_WIDTH, FOV_DEG, MODEL_TOP, frameCamera, type BramFraming } from "./bramFraming";
import { glContexts } from "../../../infra/glContexts";

export type { BramFraming };

/** Release the GPU after this long with no slot. */
const IDLE_DISPOSE_MS = 20_000;
/** A step that comes up within this of the last one leaving: he hops instead of popping in again. */
const REENTRY_MS = 1200;
/** Retry a lost context after this; give up (static art) after this many losses in a session. */
const LOST_RETRY_MS = 1000;
const MAX_LOSSES = 3;
/** A frame is due when it is at most this early (rAF jitter). */
const FRAME_SLACK_MS = 2;
/** Longest step the animation takes after a stall (a backgrounded tab). */
const MAX_DT_S = 0.1;
/** Hand the slot over only once this many frames have rendered (the first can be a blank compile frame). */
const LIVE_AFTER_FRAMES = 2;
const ENV_BLUR = 0.04;
const ENV_INTENSITY = 0.55;
const AMBIENT = { sky: 0xfff1dc, ground: 0x3a3040, intensity: 1.1 } as const;
const KEY = { color: 0xffe2b8, intensity: 2.4, pos: [-1.2, 2.2, 2.6] } as const;
const RIM = { color: 0x9fc4ff, intensity: 1.6, pos: [1.6, 1.4, -2.2] } as const;

/** One place he is shown. The view owns it; the stage draws into it while it is current. */
export interface BramSlot {
  readonly el: HTMLElement;
  readonly framing: BramFraming;
  readonly mood: BramMood;
  readonly kind: GuideKind;
  /** Viewport CSS px of what the step points at, or null. */
  target: { x: number; y: number } | null;
  speech: Speech | null;
  /** The live canvas is up (true) or gone (false): the view swaps the static art. */
  live(on: boolean): void;
  /** Mirror the canvas (he points left). */
  mirror(on: boolean): void;
}

export interface StageEnv { readonly band: () => TierBandName; readonly dpr: () => number }

export class BramStage {
  failed = false;
  /** Frames drawn so far (captures check the loop idles and stops). */
  renders = 0;
  /** A capture waiting for the next frame drawn (?debug frame strips): gets the canvas as a PNG data URL. */
  snap: ((png: string) => void) | null = null;
  private renderer: THREE.WebGLRenderer | null = null;
  private closeContext: () => void = () => {};
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(FOV_DEG, 1, 0.05, 10);
  private env: THREE.Texture | null = null;
  private model: BramModel | null = null;
  private animator: BramAnimator | null = null;
  private slot: BramSlot | null = null;
  private raf = 0;
  private lastRender = 0;
  private active = true;
  private frames = 0;
  private losses = 0;
  private lastRelease = -Infinity;
  private disposeTimer: ReturnType<typeof setTimeout> | null = null;
  private size = { w: 0, h: 0, dpr: 0, framing: "" };
  private loading = false;
  /** Shaders compiled (asynchronously where the browser can, so the game's own frames do not hitch). */
  private compiled = false;
  private entry: "enter" | "hop" | null = null;
  private mirrored = false;
  private readonly head = new THREE.Vector3();

  constructor(private readonly envi: StageEnv) {
    this.scene.add(new THREE.HemisphereLight(AMBIENT.sky, AMBIENT.ground, AMBIENT.intensity));
    const key = new THREE.DirectionalLight(KEY.color, KEY.intensity); key.position.set(...KEY.pos);
    const rim = new THREE.DirectionalLight(RIM.color, RIM.intensity); rim.position.set(...RIM.pos);
    this.scene.add(key, rim);
    if (typeof document !== "undefined") document.addEventListener("visibilitychange", () => this.poke());
    if (typeof window !== "undefined") window.addEventListener("pagehide", () => this.dispose());
  }

  /** Which model is up: the contract's, or the stand-in (null before one loads). */
  get modelName(): string | null { return this.model ? (this.model.rig.standIn ? "stand-in" : "captain_bram") : null; }

  /** The model is in and the renderer compiled: a new slot is drawn live within LIVE_AFTER_FRAMES frames. */
  get warm(): boolean { return !this.failed && this.model !== null && this.renderer !== null && this.compiled; }

  /** Starts fetching the model (no GPU work) so the first step can go live sooner. */
  preload(): void { if (!this.failed) this.ensureModel(); }

  /** Makes `slot` the one drawn into. */
  attach(slot: BramSlot): void {
    if (this.failed) return;
    if (this.slot && this.slot !== slot) this.slot.live(false);
    this.slot = slot;
    this.frames = 0;
    if (this.disposeTimer) { clearTimeout(this.disposeTimer); this.disposeTimer = null; }
    // he pops in when he can be live from the step's start (a line straight after another: a hop); a model that
    // arrives later cross-fades over the still instead
    this.entry = this.model ? (performance.now() - this.lastRelease > REENTRY_MS ? "enter" : "hop") : null;
    this.ensureModel();
    this.begin(slot);
    this.poke();
  }

  /** The slot is going away (the step ended): stop drawing, and free the GPU if nothing else comes soon. */
  detach(slot: BramSlot): void {
    if (this.slot !== slot) return;
    slot.live(false);
    this.slot = null;
    this.lastRelease = performance.now();
    this.stop();
    this.renderer?.domElement.remove();
    this.disposeTimer ??= setTimeout(() => { this.disposeTimer = null; if (!this.slot) this.dispose(); }, IDLE_DISPOSE_MS);
  }

  /** The step changed its words, its target moved, the page came back: resume the loop if it can draw. */
  poke(): void {
    if (this.raf || !this.slot || !this.model || this.failed || !this.visible()) return;
    if (!this.renderer && !this.createRenderer()) return;
    this.raf = requestAnimationFrame((t) => this.frame(t));
  }

  nudge(): void { this.animator?.nudge(performance.now()); this.poke(); }


  /** Frees the context, the environment map and the model; the next attach starts over. */
  dispose(): void {
    this.stop();
    this.dropRenderer();
    this.animator?.dispose();
    this.animator = null;
    forgetBram(this.model);
    this.model = null;
  }

  // ------------------------------------------------------------------------------------------------ internals

  private ensureModel(): void {
    if (this.model || this.loading) return;
    this.loading = true;
    loadBram().then((m) => {
      this.loading = false;
      if (this.model) return;
      this.model = m;
      this.scene.add(m.holder);
      if (this.slot) this.begin(this.slot);
      this.poke();
    }, (err) => {
      this.loading = false;
      console.warn("captain: no model, using the still renders", err);
      this.fail();
    });
  }

  /** Starts the character for the current slot: mood, gesture, speech (the entrance is timed from the first frame). */
  private begin(slot: BramSlot): void {
    if (!this.model) return;
    const now = performance.now();
    this.animator ??= new BramAnimator(this.model, Math.random, now);
    this.animator.start(slot.mood, slot.kind, slot.target !== null, now, null);
    this.animator.say(slot.speech);
  }

  private visible(): boolean {
    const s = this.slot;
    if (!s || !s.el.isConnected || (typeof document !== "undefined" && document.visibilityState === "hidden")) return false;
    return s.el.getClientRects().length > 0;
  }

  private createRenderer(): boolean {
    try {
      const dpr = bramPixelRatio(this.envi.dpr(), this.envi.band());
      // multisampling only pays below 2×: at a phone's 3× the edges are already finer than the eye
      const r = new THREE.WebGLRenderer({ alpha: true, antialias: dpr < 2, powerPreference: "low-power", premultipliedAlpha: true, preserveDrawingBuffer: false });
      r.setClearColor(0x000000, 0);
      this.closeContext = glContexts.open("bram");
      r.outputColorSpace = THREE.SRGBColorSpace;
      r.toneMapping = THREE.NeutralToneMapping;
      const canvas = r.domElement;
      canvas.className = "g-live";
      canvas.setAttribute("aria-hidden", "true");
      canvas.addEventListener("webglcontextlost", (e) => { e.preventDefault(); this.onLost(r); });
      const pm = new THREE.PMREMGenerator(r);
      this.env = pm.fromScene(new RoomEnvironment(), ENV_BLUR).texture;
      pm.dispose();
      this.scene.environment = this.env;
      this.scene.environmentIntensity = ENV_INTENSITY;
      this.renderer = r;
      this.size = { w: 0, h: 0, dpr: 0, framing: "" };
      this.frames = 0;
      this.compiled = false;
      const done = () => { if (this.renderer === r) { this.compiled = true; this.poke(); } };
      r.compileAsync(this.scene, this.camera).then(done, done);
      return true;
    } catch (err) {
      console.warn("captain: WebGL unavailable, using the still renders", err);
      this.fail();
      return false;
    }
  }

  private onLost(r: THREE.WebGLRenderer): void {
    if (r !== this.renderer) return;
    this.dropRenderer();
    if (++this.losses >= MAX_LOSSES) { this.fail(); return; }
    setTimeout(() => this.poke(), LOST_RETRY_MS);
  }

  private dropRenderer(): void {
    this.stop();
    this.slot?.live(false);
    const r = this.renderer;
    if (!r) return;
    this.renderer = null;
    this.env?.dispose();
    this.env = null;
    this.scene.environment = null;
    r.domElement.remove();
    r.dispose();
    // the context itself is only freed when asked (iOS caps live contexts and counts their memory)
    try { r.forceContextLoss(); } catch { /* already lost */ }
    this.closeContext();
  }

  private fail(): void {
    this.failed = true;
    this.slot?.live(false);
    this.dispose();
  }

  private stop(): void { if (this.raf) cancelAnimationFrame(this.raf); this.raf = 0; }

  private frame(t: number): void {
    this.raf = 0;
    const r = this.renderer, slot = this.slot, anim = this.animator;
    if (!r || !slot || !anim || !this.visible() || !this.compiled) return;
    const band = this.envi.band();
    const interval = 1000 / bramFps(this.active, band);
    if (this.frames > 0 && t - this.lastRender < interval - FRAME_SLACK_MS) { this.raf = requestAnimationFrame((n) => this.frame(n)); return; }
    const dt = this.frames === 0 ? 0 : Math.min(MAX_DT_S, (t - this.lastRender) / 1000);
    this.lastRender = t;
    const now = performance.now();
    this.fit(r, slot, band);
    this.aim(slot, now);
    if (this.frames === 0 && this.entry) { anim.enter(this.entry, now); this.entry = null; }
    anim.say(slot.speech);
    this.active = anim.update(now, dt);
    r.render(this.scene, this.camera);
    this.renders++;
    if (this.snap) { const done = this.snap; this.snap = null; done(r.domElement.toDataURL("image/png")); }
    if (++this.frames === LIVE_AFTER_FRAMES) slot.live(true);
    this.raf = requestAnimationFrame((n) => this.frame(n));
  }

  /** Canvas size and camera for the slot (only when they change). */
  private fit(r: THREE.WebGLRenderer, slot: BramSlot, band: TierBandName): void {
    if (r.domElement.parentNode !== slot.el) slot.el.append(r.domElement);
    const w = slot.el.clientWidth, h = slot.el.clientHeight, dpr = bramPixelRatio(this.envi.dpr(), band);
    if (w === this.size.w && h === this.size.h && dpr === this.size.dpr && slot.framing === this.size.framing) return;
    this.size = { w, h, dpr, framing: slot.framing };
    r.setPixelRatio(dpr);
    r.setSize(Math.max(1, w), Math.max(1, h), false);
    frameCamera(this.camera, slot.framing, w, h, this.model?.neckY ?? DEFAULT_NECK_Y, this.model?.headWidth ?? DEFAULT_HEAD_WIDTH);
  }

  /** Target relative to his eyes on screen; mirrored when he points at something to his left of the bust. */
  private aim(slot: BramSlot, now: number): void {
    const anim = this.animator!;
    if (!slot.target) { anim.aim(null, now); return; }
    const rect = slot.el.getBoundingClientRect();
    // his eyes: halfway up the head
    this.head.set(0, ((this.model?.neckY ?? DEFAULT_NECK_Y) + MODEL_TOP) / 2, 0).project(this.camera);
    const ex = rect.left + ((this.head.x + 1) / 2) * rect.width, ey = rect.top + ((1 - this.head.y) / 2) * rect.height;
    const dx = slot.target.x - ex, dy = slot.target.y - ey;
    // hysteresis (M45): a target moving about straight below him (a raider on the road) flipped him every frame
    const mirror = anim.mood === "point" && (this.mirrored ? dx < MIRROR_MARGIN_PX : dx < -MIRROR_MARGIN_PX);
    if (mirror !== this.mirrored) { this.mirrored = mirror; slot.mirror(mirror); }
    anim.aim({ dx, dy, mirrored: mirror }, now);
  }
}

/** He only turns to point left when the target is clearly left of him, so a target straight below does not flip him. */
const MIRROR_MARGIN_PX = 40;
